import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Plus, AlertTriangle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, CardContent, TableSkeleton } from '@/components/ui';
import { CursorPagination } from '@/components/ui/cursor-pagination';
import { ApiErrorDisplay } from '@/components/features/error';
import { ApiError } from '@/lib/api/client';
import {
  TransactionFilters,
  activeSearchFromFilters,
} from '@/components/features/transactions/transaction-filters';
import { SearchNotice } from '@/components/features/transactions/search-notice';
import { TransactionTable, type TransactionSortColumn } from '@/components/features/transactions/transaction-table';
import { useAdminTransactions, adminTransactionKeys } from '@/hooks/use-admin-transactions';
import { adminSubscriptionKeys } from '@/hooks/use-admin-subscriptions';
import { DEFAULT_SUB_LIMIT } from '@/hooks/use-subscription-filter-params';
import {
  useTransactionSearchCascade,
  FULL_TXN_ID_PATTERN,
  FULL_SUBSCRIPTION_ID_PATTERN,
  type CascadeStage,
} from '@/hooks/use-transaction-search-cascade';
import { useTransactionFilterParams } from '@/hooks/use-transaction-filter-params';
import { useSortState } from '@/hooks/use-sort-state';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useAuth } from '@/hooks/use-auth';
import { ROLE_GROUPS } from '@/types/auth';
import { sortItems } from '@/lib/utils/sort';
import { filterByDateRange, filterByAmountRange, euroToCents } from '@/lib/utils/client-filters';
import type { ListAdminTransactionsParams } from '@/types/admin-transaction';

// Spec 479/#482: Voll-Miss/Typwechsel raeumt alle vier Such-Achsen aus der URL.
const CLEARED_SEARCH_PARAMS = {
  txId: undefined,
  internetqTransactionId: undefined,
  customerReference: undefined,
  customerReferenceExact: undefined,
} as const;

// Spec 409/479: Schluessel des Server-Filterzustands — Pager-Reset + Discard-
// Guard-Snapshot. Spec 481 D10: auch der Ohne-Filter-Neustart baut seinen
// Snapshot hierueber aus dem GERAEUMTEN Zustand (nie aus dem stale Ref, Trap 7).
function buildServerFiltersKey(f: ListAdminTransactionsParams): string {
  return JSON.stringify({
    merchantId: f.merchantId,
    service: f.service,
    status: f.status,
    operator: f.operator,
    txId: f.txId,
    // Spec 475/#482: die Server-Suchbegriffe resetten den Pager wie jeder Server-Filter.
    internetqTransactionId: f.internetqTransactionId,
    customerReference: f.customerReference,
    customerReferenceExact: f.customerReferenceExact,
    flowType: f.flowType,
    gatewayMode: f.gatewayMode,
    limit: f.limit,
  });
}

// Spec 481 D10 / #482: „keiner ID-Form" = weder TXN_- noch SIQ/sub_-Gate
// matcht; das IQ-Laengen-Gate zaehlt NICHT (kein Format-Diskriminator). Fuer
// solche Begriffe ist die Referenz-Achse die einzige realistische Hoffnung —
// und die trifft seit #482 (v2.18-GSI) nur noch exakt. Die Voll-Miss-Meldung
// nennt die Exakt-Semantik dann explizit (teilweise gepastete Referenzen).
function matchesNoIdForm(term: string): boolean {
  return !FULL_TXN_ID_PATTERN.test(term) && !FULL_SUBSCRIPTION_ID_PATTERN.test(term);
}

export function TransactionsPage() {
  const { t } = useTranslation('transactions');
  const navigate = useNavigate();
  const location = useLocation();
  // Spec 0220: Merchant-Spalte nur fuer Platform-Rollen.
  const { showMerchant } = useMerchantScope();
  // Issue #404: "Transaktion auslösen" nur für Rollen mit Trigger-Recht
  // (deckungsgleich mit der ProtectedRoute /transactions/trigger + Backend
  // transactions:trigger[:own]). merchant_viewer/platform_ops sehen den Button nicht.
  const { hasRole } = useAuth();
  const canTriggerTransaction = hasRole(ROLE_GROUPS.TX_TRIGGER);

  // Issue #300: Filter-State liegt in der URL (useSearchParams), nicht mehr in
  // lokalem useState — so ueberleben Filter den Wechsel in die Detailansicht,
  // sind reload-/teilbar und per Reset trivial leerbar.
  const {
    filters,
    amountMin,
    amountMax,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setFilters,
    setAmountMin,
    setAmountMax,
    setDateRange,
    reset,
  } = useTransactionFilterParams();

  // Blaetter-Cursor bleibt lokal — Spec 300: beim Zurueckkehren genuegt die
  // erste Seite des Filters (Filter werden erhalten, Blaetter-Tiefe nicht).
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  // Spec 409: der Pager wird bei JEDER Server-Filter-Aenderung zurueckgesetzt — auch
  // bei externen URL-Mutationen (Back/Forward, Deep-Link, geteilter Link). Sonst bliebe
  // ein Cursor aus einem anderen Filtersatz aktiv (stale-cursor gegen die neue Liste).
  // Der lokale Reset in handleFiltersChange/handleReset bleibt zusaetzlich (sofortig).
  const serverFiltersKey = buildServerFiltersKey(filters);
  const prevFiltersKeyRef = useRef(serverFiltersKey);
  useEffect(() => {
    if (prevFiltersKeyRef.current !== serverFiltersKey) {
      prevFiltersKeyRef.current = serverFiltersKey;
      setCursorStack([]);
    }
  }, [serverFiltersKey]);

  // Sortierung (client-seitig, Default: Datum desc)
  const { sort, toggleSort } = useSortState<TransactionSortColumn>('createdAt', 'desc');

  const { data, isLoading, error, refetch } = useAdminTransactions({
    ...filters,
    cursor: currentCursor,
  });

  // Spec 479: transienter Such-Ausgang (Voll-Miss/Fehler) — bewusst NICHT in
  // der URL: ein geteilter Miss-Link wuerde nur den teuersten Pfad erneut
  // feuern (Spec D5). Jede Server-Filter-Aenderung raeumt die Meldung.
  // Spec 481 D10 / #482: der Fehler-Fall traegt die gescheiterte Stufe; die
  // ID-Form-Einordnung braucht nur noch der Voll-Miss (Exakt-Eingabe-Hinweis)
  // und wird dort aus dem Begriff berechnet.
  const [searchNotice, setSearchNotice] = useState<
    | { kind: 'miss'; term: string; sawTruncated: boolean }
    | { kind: 'error'; term: string; stage: CascadeStage }
    | null
  >(null);

  // Server-Filter Handler (resettet Cursor)
  const handleFiltersChange = useCallback((newFilters: ListAdminTransactionsParams) => {
    setSearchNotice(null);
    setFilters(newFilters);
    setCursorStack([]);
  }, [setFilters]);

  // Spec 479: Ein-Feld-Suche — die Seite besitzt den Entwurf (kontrolliertes
  // Control) und spiegelt externe URL-Aenderungen (Deep-Link, Back/Forward,
  // Reset) hinein. Beim Voll-Miss bleibt der Begriff im Feld stehen, obwohl
  // die Such-Params aus der URL geraeumt werden (keepDraftRef ueberspringt
  // genau diesen einen selbst ausgeloesten Sync).
  const activeSearch = activeSearchFromFilters(filters);
  const [searchDraft, setSearchDraft] = useState(activeSearch.term);
  const keepDraftRef = useRef(false);
  useEffect(() => {
    if (keepDraftRef.current) {
      keepDraftRef.current = false;
      return;
    }
    setSearchDraft(activeSearch.term);
  }, [activeSearch.term]);

  // Spec 479: Kaskaden-Verdrahtung. Der Hook orchestriert die Stufen; die
  // Seite wendet das Outcome an (URL-Schreiben nur bei Treffer, Cache-Seeding
  // gegen Doppel-Requests, Stufe-2-Direktnavigation). Der Discard-Guard
  // vergleicht den serverFiltersKey-Snapshot vom Submit gegen den aktuellen
  // Stand (Ref wird pro Render aktualisiert) — externe Filter-Drift
  // (Back/Forward) verwirft das Ergebnis statt es anzuwenden (Spec D6).
  const queryClient = useQueryClient();
  const cascade = useTransactionSearchCascade();
  const serverFiltersKeyRef = useRef(serverFiltersKey);
  serverFiltersKeyRef.current = serverFiltersKey;

  // Aktive Server-Filter OHNE die Such-Params — die Basis jedes
  // Kaskaden-Requests (Cache-Key-Kongruenz zur Seiten-Query, Spec D6).
  const cascadeBaseFilters = useMemo(() => {
    const {
      txId: _txId,
      internetqTransactionId: _iqTxId,
      customerReference: _customerRef,
      customerReferenceExact: _customerRefExact,
      ...rest
    } = filters;
    return rest;
  }, [filters]);

  // Spec 481 D10: gemeinsamer Kaskaden-Runner fuer den normalen Submit UND den
  // Ohne-Filter-Neustart — beide unterscheiden sich nur im Basis-Zustand
  // (Filter, Snapshot, from-Basis), die Outcome-Anwendung ist identisch.
  const runCascadeSearch = useCallback(
    async (
      term: string,
      ctx: {
        /** Server-Filter OHNE Such-Params — Basis jedes Kaskaden-Requests (D6). */
        baseFilters: ListAdminTransactionsParams;
        /** ERWARTETER serverFiltersKey des Laufs (beim Ohne-Filter-Neustart: der geraeumte Zustand). */
        snapshotKey: string;
        merchantScopeHint?: string;
        /** Basis fuer den Detail-`from`-State (wird um die Such-Params bereinigt). */
        fromSearch: string;
        /** Steht ein Such-Param in der URL, den ein Voll-Miss raeumen muss? */
        urlHasSearchTerm: boolean;
      },
    ) => {
      // Trap 7: der Guard vergleicht gegen den ERWARTETEN Zustand. Beim
      // Ohne-Filter-Neustart hat der URL-Write noch nicht re-gerendert —
      // eager setzen; der naechste Render bestaetigt den Wert, jede externe
      // Drift (Back/Forward) invalidiert ihn wie gehabt.
      serverFiltersKeyRef.current = ctx.snapshotKey;
      const outcome = await cascade.run(term, {
        baseFilters: ctx.baseFilters,
        merchantScopeHint: ctx.merchantScopeHint,
        isStillValid: () => serverFiltersKeyRef.current === ctx.snapshotKey,
      });

      switch (outcome.type) {
        case 'hit': {
          // Erst seeden, dann URL schreiben: die Seiten-Query findet das
          // Ergebnis frisch im Cache (staleTime 30 s) — genau EIN Upstream-
          // Request pro gelaufener Stufe, kein zweiter ~29-s-Scan (Spec D6).
          queryClient.setQueryData(adminTransactionKeys.list(outcome.params), outcome.response);
          handleFiltersChange({ ...ctx.baseFilters, [outcome.axis]: term });
          break;
        }
        case 'detailHit': {
          // Stufe-2-Treffer: Direktnavigation ins Detail (Spec D4). `from` wird
          // um die drei Such-Params bereinigt — der In-App-Zurueck darf keinen
          // konzeptionell ueberholten Alt-Scan wieder abfeuern und stellt den
          // Suchbegriff nicht wieder her (dokumentierte Semantik).
          const detail = outcome.detail;
          queryClient.setQueryData(adminTransactionKeys.detail(detail.transactionId), detail);
          const cleaned = new URLSearchParams(ctx.fromSearch);
          cleaned.delete('txId');
          cleaned.delete('internetqTransactionId');
          cleaned.delete('customerReference');
          cleaned.delete('customerReferenceExact');
          const cleanedFrom = cleaned.toString() ? `?${cleaned.toString()}` : '';
          navigate(`/transactions/${detail.transactionId}`, {
            state: {
              ...(detail.merchantId ? { merchantId: detail.merchantId } : {}),
              from: cleanedFrom,
            },
          });
          break;
        }
        case 'subscriptionHit': {
          // Spec 481 D3: Landung auf der per subId gefilterten Subscriptions-
          // Uebersicht — URL, Seed-Key und Expand-State tragen die KANONISCHE
          // ID aus dem Response (D4), nie den Roh-Begriff. Der Seed-Key muss
          // exakt dem entsprechen, was die Subscriptions-Seite nach der Landung
          // berechnet: nur subId + deren DEFAULT_SUB_LIMIT (20, nicht das
          // TX-limit!), keine TX-Facetten (Trap 1). Die Ein-Zeilen-Response ist
          // typgleich (Liste und Detail tragen dieselbe AdminSubscription-Shape).
          const sub = outcome.detail;
          queryClient.setQueryData(
            adminSubscriptionKeys.list({
              subId: sub.subscriptionId,
              limit: DEFAULT_SUB_LIMIT,
              cursor: undefined,
            }),
            {
              data: [sub],
              pagination: { nextCursor: null, hasMore: false, limit: DEFAULT_SUB_LIMIT },
            },
          );
          // Kein TX-URL-Write, kein `from` (D8): das Ziel ist eine Top-Level-
          // Listen-Seite ohne Zurueck-Knopf; Browser-Back landet auf der
          // unveraenderten TX-URL. expandSubscriptionId wird dort einmalig beim
          // Mount konsumiert (Auto-Expand der Treffer-Zeile).
          navigate(`/subscriptions?subId=${encodeURIComponent(sub.subscriptionId)}`, {
            state: { expandSubscriptionId: sub.subscriptionId },
          });
          break;
        }
        case 'miss': {
          // Kein Such-Param bleibt in der URL (Spec D5); der Begriff bleibt im
          // Feld (keepDraftRef). Meldung NACH dem Raeumen setzen —
          // handleFiltersChange raeumt searchNotice.
          if (ctx.urlHasSearchTerm) {
            keepDraftRef.current = true;
            handleFiltersChange({ ...ctx.baseFilters, ...CLEARED_SEARCH_PARAMS, cursor: undefined });
          }
          setSearchNotice({ kind: 'miss', term, sawTruncated: outcome.sawTruncated });
          break;
        }
        case 'error':
          // Spec 481 D10: gescheiterte Stufe fuer die differenzierte Meldung.
          setSearchNotice({ kind: 'error', term, stage: outcome.stage });
          break;
        case 'discarded':
          break;
      }
    },
    [cascade, queryClient, handleFiltersChange, navigate],
  );

  const handleSearchSubmit = useCallback(async () => {
    const term = searchDraft.trim();
    setSearchNotice(null);
    if (!term) {
      // Leeren + Submit: alle drei Such-Params raeumen, keine Kaskade.
      if (activeSearch.term) {
        handleFiltersChange({ ...filters, ...CLEARED_SEARCH_PARAMS, cursor: undefined });
      }
      return;
    }

    await runCascadeSearch(term, {
      baseFilters: cascadeBaseFilters,
      snapshotKey: serverFiltersKeyRef.current,
      merchantScopeHint: filters.merchantId,
      fromSearch: location.search,
      urlHasSearchTerm: !!activeSearch.term,
    });
  }, [
    searchDraft,
    activeSearch.term,
    filters,
    cascadeBaseFilters,
    runCascadeSearch,
    handleFiltersChange,
    location.search,
  ]);

  // Spec 481 D10 (Owner-Fund 2): Ein-Klick „ohne Filter erneut suchen" —
  // raeumt die Server-Facetten (limit bleibt; Datum/Betrag sind page-lokal
  // und nicht Teil von `filters`) und startet die Kaskade mit demselben
  // Begriff gegen den ungefilterten Zustand neu. Snapshot + baseFilters
  // stammen aus dem GERAEUMTEN Zustand (Trap 7, nie der stale Ref).
  const handleRetryWithoutFilters = useCallback(async () => {
    const term = (searchNotice?.term ?? searchDraft).trim();
    if (!term || cascade.isRunning) return;
    setSearchNotice(null);
    const cleared: ListAdminTransactionsParams = { limit: filters.limit };
    if (activeSearch.term) keepDraftRef.current = true;
    setSearchDraft(term);
    handleFiltersChange(cleared);
    // `from`-Basis = die URL NACH der Facetten-Raeumung: Server-Facetten +
    // Such-Params raus, aber die page-lokalen Datum/Betrag-Params und ein
    // non-default limit BLEIBEN — ein Stufe-2-Treffer des Neustarts darf sie
    // im Detail-Zurueck nicht verlieren (codex-Review Phase 3).
    const clearedFrom = new URLSearchParams(location.search);
    for (const key of [
      'merchantId', 'service', 'status', 'operator', 'flowType', 'gatewayMode',
      'serviceId', 'cursor', 'txId', 'internetqTransactionId', 'customerReference',
      'customerReferenceExact',
    ]) {
      clearedFrom.delete(key);
    }
    await runCascadeSearch(term, {
      baseFilters: cleared,
      snapshotKey: buildServerFiltersKey(cleared),
      merchantScopeHint: undefined,
      fromSearch: clearedFrom.toString() ? `?${clearedFrom.toString()}` : '',
      urlHasSearchTerm: false,
    });
  }, [
    searchNotice?.term,
    searchDraft,
    cascade.isRunning,
    filters.limit,
    activeSearch.term,
    handleFiltersChange,
    runCascadeSearch,
    location.search,
  ]);

  // Deep-Link-Fehlerfall (Seiten-Query gescheitert): Facetten raeumen, die
  // aktive Such-Achse BEHALTEN — die Seiten-Query refetcht von selbst, ein
  // Kaskaden-Lauf waere doppelt (Spec 481 D10).
  const handleClearFacetsKeepSearch = useCallback(() => {
    handleFiltersChange({
      limit: filters.limit,
      [activeSearch.axis]: activeSearch.term,
    });
  }, [handleFiltersChange, filters.limit, activeSearch.axis, activeSearch.term]);

  const handleNextPage = useCallback((cursor: string) => {
    setCursorStack((prev) => [...prev, cursor]);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  const handleReset = useCallback(() => {
    setSearchNotice(null);
    reset();
    setCursorStack([]);
  }, [reset]);

  const handleRowClick = useCallback((transactionId: string, merchantId?: string) => {
    // Issue #300: aktuelle Listen-Query als `from` mitgeben, damit der
    // Detail-Zurueck-Button die Filter wiederherstellt.
    navigate(`/transactions/${transactionId}`, {
      state: { ...(merchantId ? { merchantId } : {}), from: location.search },
    });
  }, [navigate, location.search]);

  // Pipeline: API-Daten → Client-Filter → Sort
  const { filteredItems, totalCount } = useMemo(() => {
    if (!data?.data) return { filteredItems: [], totalCount: 0 };
    const items = data.data;
    const total = items.length;

    let result = filterByDateRange(items, 'createdAt', dateFrom, dateTo);
    result = filterByAmountRange(result, 'grossAmount', euroToCents(amountMin), euroToCents(amountMax));
    result = sortItems(result, sort.column, sort.direction);

    return { filteredItems: result, totalCount: total };
  }, [data?.data, dateFrom, dateTo, amountMin, amountMax, sort.column, sort.direction]);

  const hasActiveClientFilters = dateFrom || dateTo || amountMin || amountMax;
  const showFilterHint = hasActiveClientFilters && filteredItems.length < totalCount;

  // Issue #302: Das Merchant×Status-Warnbanner (#298) ist obsolet — die Facade
  // bedient die Kombination seit #478/#483 vollstaendig (prod-verifiziert
  // 2026-06-14). hasMore ist damit wieder aussagekraeftig.
  const showMoreAvailable = data?.pagination.hasMore;

  // Spec 475: „Suche aktiv" direkt aus den Filter-Props (kein neues State-Flag).
  const searchActive = !!(
    filters.txId ||
    filters.internetqTransactionId ||
    filters.customerReference ||
    filters.customerReferenceExact
  );
  // Spec 479: aktive Server-Filter (ohne Suchbegriffe) grenzen die Listen-
  // Stufen der Kaskade ein — die Voll-Miss-Meldung benennt das als Ursache.
  const hasServerFilterConstraint = !!(
    filters.merchantId ||
    filters.service ||
    filters.status ||
    filters.operator ||
    filters.flowType ||
    filters.gatewayMode
  );
  // Die spezifische Such-Fehlermeldung gilt NUR fuer den Timeout-/Ausfall-Pfad
  // (502/503/504 bzw. Netzwerkfehler = kein ApiError). Ein 400/403 bleibt beim
  // generischen ApiErrorDisplay — sonst maskiert der Eingrenzungs-Hinweis die
  // eigentliche Fehlerursache.
  const searchTimeoutError =
    searchActive &&
    !!error &&
    (!(error instanceof ApiError) || [502, 503, 504].includes(error.status));

  return (
    <div>
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.refresh')}
            </Button>
            {canTriggerTransaction && (
              <Button size="sm" onClick={() => navigate('/transactions/trigger')}>
                <Plus className="mr-2 h-4 w-4" />
                {t('list.newTransaction')}
              </Button>
            )}
          </div>
        }
      />

      <TransactionFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        amountMin={amountMin}
        amountMax={amountMax}
        onAmountMinChange={setAmountMin}
        onAmountMaxChange={setAmountMax}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={setDateRange}
        canReset={hasActiveFilters}
        onReset={handleReset}
        searchTerm={searchDraft}
        onSearchTermChange={setSearchDraft}
        onSearchSubmit={handleSearchSubmit}
        isSearching={cascade.isRunning}
        runningStage={cascade.runningStage}
      />

      {/* Spec 479/481: Voll-Miss der Kaskade — transiente Meldung (kein
          URL-Zustand, D5) mit durchsuchten Achsen; aktive Filter + Teilergebnis
          werden benannt, die Ohne-Filter-Affordanz loest die Eingrenzung.
          Die Liste darunter zeigt ehrlich den unge-suchten Zustand. */}
      {searchNotice?.kind === 'miss' && (
        <SearchNotice
          variant="miss"
          term={searchNotice.term}
          termMatchesNoIdForm={matchesNoIdForm(searchNotice.term)}
          sawTruncated={searchNotice.sawTruncated}
          hasServerFilterConstraint={hasServerFilterConstraint}
          onRetryWithoutFilters={handleRetryWithoutFilters}
        />
      )}

      {/* Spec 479/481: Infrastruktur-Abbruch der Kaskade (Stop-on-Error, D3) —
          differenzierte Meldung (gescheiterte Achse, Referenz-Limit, Filter)
          mit Plain-Retry + Ohne-Filter-Neustart (D10). */}
      {searchNotice?.kind === 'error' && (
        <SearchNotice
          variant="error"
          term={searchNotice.term}
          stage={searchNotice.stage}
          hasServerFilterConstraint={hasServerFilterConstraint}
          onRetry={handleSearchSubmit}
          onRetryWithoutFilters={handleRetryWithoutFilters}
        />
      )}

      {/* Spec 409: BFF-Scan-Degradation — `truncated` (Seitenschranke erreicht) bzw.
          `unavailable` (Scan fehlgeschlagen). Getrennt vom page-lokalen filterHint
          (Datum/Betrag), nicht vermischt. */}
      {!isLoading && !error && data && (data.truncated || data.unavailable) && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {data.unavailable
              ? t('list.banner.unavailable', 'Filtern momentan nicht verfügbar. Bitte erneut versuchen.')
              : t('list.banner.truncated', 'Zu viele Treffer — Teilergebnis. Filter weiter eingrenzen.')}
          </span>
        </div>
      )}

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={7} />
          </CardContent>
        </Card>
      )}

      {/* Spec 475/481: Fehler bei aktiver ID-Suche (Deep-Link/Seiten-Query) →
          dieselbe differenzierte Meldung wie die Kaskade (Achse aus dem
          URL-Param, Referenz-Limit, Filter). Ohne-Filter raeumt hier NUR die
          Facetten — die Such-Achse bleibt, die Query refetcht selbst (D10). */}
      {error && searchTimeoutError ? (
        <SearchNotice
          variant="error"
          term={activeSearch.term}
          stage={activeSearch.axis}
          hasServerFilterConstraint={hasServerFilterConstraint}
          onRetry={() => refetch()}
          onRetryWithoutFilters={handleClearFacetsKeepSearch}
        />
      ) : (
        error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />
      )}

      {!isLoading && !error && data && (
        <Card>
          {/* Spec 296: eine einzige, widerspruchsfreie Mengenzeile. Bei aktiven
              Client-Filtern der bestehende filterHint (shown/total), sonst der
              ehrliche Geladen-Zaehler. */}
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm text-muted-foreground">
            <span>
              {showFilterHint
                ? t('list.filterHint', {
                    shown: filteredItems.length,
                    total: totalCount,
                    defaultValue: 'Zeige {{shown}} von {{total}} Einträgen auf dieser Seite',
                  })
                : t('list.loadedCount', {
                    count: filteredItems.length,
                    defaultValue: '{{count}} geladen',
                  })}
              {showMoreAvailable && (
                <> · {t('list.moreAvailable', { defaultValue: 'weitere verfügbar' })}</>
              )}
            </span>
            {/* Spec 479: Treffer-Achsen-Transparenz — aus dem aktiven URL-Param
                abgeleitet, funktioniert damit auch fuer Deep-Links. */}
            {searchActive && (
              <Badge variant="secondary">
                {t('list.search.hitVia', { axis: t(`list.search.stages.${activeSearch.axis}`) })}
              </Badge>
            )}
          </div>
          <TransactionTable
            transactions={filteredItems}
            onRowClick={handleRowClick}
            sort={sort}
            onToggleSort={toggleSort}
            showMerchant={showMerchant}
          />
          <CursorPagination
            hasMore={data.pagination.hasMore}
            nextCursor={data.pagination.nextCursor}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
            hasPrevious={cursorStack.length > 0}
            isLoading={isLoading}
          />
        </Card>
      )}
    </div>
  );
}
